/**
 * Structured Logger
 *
 * JSON logging with levels, request context, and environment-aware output.
 * - Development: pretty-printed console output
 * - Production: structured JSON for log aggregation (Datadog, CloudWatch, etc.)
 *
 * Usage:
 *   import { logger } from "@/lib/logger";
 *   logger.info("thing happened", { userId });
 *
 *   import { createLogger } from "@/lib/logger";
 *   const logger = createLogger("stripe-issuing");
 *   logger.error("card failed", { cardId }, err);
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogContext {
  userId?: string;
  practiceId?: string;
  path?: string;
  method?: string;
  statusCode?: number;
  durationMs?: number;
  [key: string]: unknown;
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const MIN_LEVEL = LOG_LEVELS[(process.env.LOG_LEVEL as LogLevel) || "info"] ?? LOG_LEVELS.info;
const IS_PRODUCTION = process.env.NODE_ENV === "production";

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= MIN_LEVEL;
}

function formatEntry(entry: LogEntry): string {
  if (IS_PRODUCTION) {
    return JSON.stringify(entry);
  }

  // Development: human-readable format
  const parts = [
    `[${entry.timestamp}]`,
    `[${entry.level.toUpperCase()}]`,
    entry.message,
  ];

  if (entry.context) {
    const ctx = { ...entry.context };
    // Only show non-empty context keys
    const contextStr = Object.entries(ctx)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${k}=${v}`)
      .join(" ");
    if (contextStr) parts.push(`| ${contextStr}`);
  }

  if (entry.error) {
    parts.push(`\n  Error: ${entry.error.message}`);
    if (entry.error.stack) {
      parts.push(`\n  ${entry.error.stack}`);
    }
  }

  return parts.join(" ");
}

function log(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
  if (!shouldLog(level)) return;

  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    context: context && Object.keys(context).length > 0 ? context : undefined,
    error: error
      ? {
          name: error.name,
          message: error.message,
          stack: IS_PRODUCTION ? undefined : error.stack,
        }
      : undefined,
  };

  const formatted = formatEntry(entry);

  switch (level) {
    case "debug":
      console.debug(formatted);
      break;
    case "info":
      console.log(formatted);
      break;
    case "warn":
      console.warn(formatted);
      break;
    case "error":
      console.error(formatted);
      break;
  }
}

export const logger = {
  debug: (message: string, context?: LogContext) => log("debug", message, context),
  info: (message: string, context?: LogContext) => log("info", message, context),
  warn: (message: string, context?: LogContext, error?: Error) => log("warn", message, context, error),
  error: (message: string, context?: LogContext, error?: Error) => log("error", message, context, error),
};

/**
 * Create a module-scoped logger that automatically includes the module name
 * in every log entry's context. Use this at the top of each lib file:
 *
 *   const logger = createLogger("stripe-issuing");
 */
export function createLogger(module: string) {
  return {
    debug: (message: string, context?: LogContext) =>
      log("debug", message, { module, ...context }),
    info: (message: string, context?: LogContext) =>
      log("info", message, { module, ...context }),
    warn: (message: string, context?: LogContext, error?: Error) =>
      log("warn", message, { module, ...context }, error),
    error: (message: string, context?: LogContext, error?: Error) =>
      log("error", message, { module, ...context }, error),
  };
}

export default logger;

/**
 * Create a logger with pre-bound context (e.g., for a specific API route handler).
 */
export function createRequestLogger(baseContext: LogContext) {
  return {
    debug: (message: string, extra?: LogContext) =>
      log("debug", message, { ...baseContext, ...extra }),
    info: (message: string, extra?: LogContext) =>
      log("info", message, { ...baseContext, ...extra }),
    warn: (message: string, extra?: LogContext, error?: Error) =>
      log("warn", message, { ...baseContext, ...extra }, error),
    error: (message: string, extra?: LogContext, error?: Error) =>
      log("error", message, { ...baseContext, ...extra }, error),
  };
}

/**
 * Extract the request ID from an incoming request header.
 * Middleware sets X-Request-Id on every response; API route handlers
 * can read it back via this helper.
 */
export function getRequestId(request: Request): string {
  return request.headers.get("X-Request-Id") || "unknown";
}

/**
 * Log an API request with duration tracking.
 * Usage:
 *   const end = logRequest(request);
 *   // ... handle request ...
 *   end(200);
 */
export function logRequest(
  request: Request,
  context?: { userId?: string; practiceId?: string }
) {
  const start = Date.now();
  const url = new URL(request.url);

  return (statusCode: number) => {
    const durationMs = Date.now() - start;
    log(statusCode >= 500 ? "error" : statusCode >= 400 ? "warn" : "info", `${request.method} ${url.pathname}`, {
      method: request.method,
      path: url.pathname,
      statusCode,
      durationMs,
      ...context,
    });
  };
}
